import { z } from 'zod'
import { apiEnvelope, pageSchema } from '@/shared/api/api-contract'

export const fileTagSchema = z.object({
  tagId: z.number().int(),
  name: z.string(),
})

export const fileAssetSchema = z.object({
  fileId: z.number().int(),
  businessType: z.enum(['AVATAR', 'AI_INPUT', 'AI_GENERATED', 'SITE_PUBLIC_ASSET']),
  mediaType: z.enum(['IMAGE', 'VIDEO', 'AUDIO', 'DOCUMENT', 'OTHER']),
  filename: z.string(),
  contentType: z.string(),
  size: z.number().int(),
  status: z.enum(['PENDING', 'AVAILABLE']),
  previewUrl: z.string().nullish(),
  createdAt: z.string(),
  favorite: z.boolean(),
  tags: z.array(fileTagSchema),
  folderId: z.number().int().default(0),
})

export const fileFolderSchema = z.object({
  id: z.number().int(),
  parentId: z.number().int(),
  name: z.string(),
  createdAt: z.string(),
  updatedAt: z.string(),
})

export interface FileFolderTree {
  id: number
  parentId: number
  name: string
  children: FileFolderTree[]
}

export const fileFolderTreeSchema: z.ZodType<FileFolderTree> = z.lazy(() => z.object({
  id: z.number().int(),
  parentId: z.number().int(),
  name: z.string(),
  children: z.array(fileFolderTreeSchema),
}))

const fileEntryBaseSchema = z.object({
  id: z.number().int(),
  name: z.string(),
  parentFolderId: z.number().int(),
  createdAt: z.string(),
})

export const fileEntrySchema = z.discriminatedUnion('type', [
  fileEntryBaseSchema.extend({ type: z.literal('FILE'), file: fileAssetSchema }),
  fileEntryBaseSchema.extend({ type: z.literal('FOLDER'), file: z.null().optional() }),
])

export const fileUploadEnvelopeSchema = apiEnvelope(z.object({
  fileId: z.number().int(),
  uploadMethod: z.string(),
  uploadUrl: z.string(),
  headers: z.record(z.string(), z.string()).default({}),
  expiresAt: z.string().optional(),
}))

export const fileEnvelopeSchema = apiEnvelope(fileAssetSchema)
const legacyFileListEnvelopeSchema = apiEnvelope(pageSchema(fileAssetSchema))
export const fileEntryListEnvelopeSchema = apiEnvelope(pageSchema(fileEntrySchema))
export const fileListEnvelopeSchema = z.union([legacyFileListEnvelopeSchema, fileEntryListEnvelopeSchema]).transform((envelope) => ({
  ...envelope,
  data: {
    ...envelope.data,
    items: envelope.data.items.flatMap((item) => 'type' in item ? item.type === 'FILE' ? [item.file] : [] : [item]),
  },
}))
export const fileTagEnvelopeSchema = apiEnvelope(fileTagSchema)
export const fileTagListEnvelopeSchema = apiEnvelope(z.array(fileTagSchema))
export const fileFolderEnvelopeSchema = apiEnvelope(fileFolderSchema)
export const fileFolderTreeEnvelopeSchema = apiEnvelope(z.array(fileFolderTreeSchema))
export const filePreviewEnvelopeSchema = apiEnvelope(z.object({
  fileId: z.number().int(),
  fileType: z.string().nullish(),
  previewUrl: z.string(),
}))

export type FileAsset = z.infer<typeof fileAssetSchema>
export type FileAssetPage = z.infer<typeof fileListEnvelopeSchema>['data']
export type FileTag = z.infer<typeof fileTagSchema>
export type FileFolder = z.infer<typeof fileFolderSchema>
export type FileEntry = z.infer<typeof fileEntrySchema>
export type FileEntryPage = z.infer<typeof fileEntryListEnvelopeSchema>['data']
